import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { SEOHead } from "@/features/shared/SEOHead";
import { CapabilitiesSection } from "@/components/Homepage/CapabilitiesSection";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRight, Database, Target, Search, Mic, Layers, Zap, TrendingUp, Activity, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { MissedCallTextback } from "@/components/shared/MissedCallTextback";
import { LeadPipelineMockup } from "@/components/shared/LeadPipelineMockup";
import { LocalPackMockup } from "@/components/shared/LocalPackMockup";
import { ScaleRoasMockup } from "@/components/shared/ScaleRoasMockup";
import { VoiceAIMockup } from "@/components/shared/VoiceAIMockup";
import { useRevealObserver } from "@/hooks/useRevealObserver";

const Solutions = () => {
  useRevealObserver();

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead
        title="Growth Systems, Not Services | Frayze"
        description="Frayze builds connected growth systems that evolve as your business matures. From CRM foundations to enterprise intelligence."
        keywords="growth systems, business automation, CRM foundation, marketing system, visibility layer, enterprise intelligence, Thunder Bay"
        canonical="https://frayze.ca/solutions/"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://frayze.ca/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Solutions",
              "item": "https://frayze.ca/solutions/"
            }
          ]
        }}
      />
      <Header />

      <main>
        {/* HERO SECTION */}
        <section className="relative pt-40 pb-20 md:pt-48 md:pb-24 overflow-hidden bg-background border-b">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-500/5 via-background to-background" />
          <div className="container relative mx-auto px-6 text-center max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 text-balance">
              One Connected Growth System: Built in the Right Order
            </h1>

            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto text-balance font-medium">
              Not five disconnected tools. Five layers of one system: CRM, marketing, visibility, scale, and intelligence, each built on the one before it.
            </p>
            <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto text-balance">
              You start where you’re leaking and add layers as volume demands. Built for lead-driven businesses: from your first lead to enterprise scale.
            </p>

            <div className="inline-block p-4 rounded-xl bg-muted/40 border mb-10">
              <span className="font-bold text-foreground">One system instead of five disconnected tools, so nothing falls through the cracks.</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="font-bold" asChild>
                <Link to="/book-consultation/">
                  Book a Strategy Session <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/pricing/">View Pricing</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* CONCEPT SECTION */}
        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-6 max-w-3xl text-center">
            <h2 className="reveal reveal-delay-1 text-3xl font-bold mb-4">How Frayze Is Structured</h2>
            <p className="reveal reveal-delay-2 text-xl font-bold text-indigo-600 mb-6">One System. Five Layers.</p>
            <p className="reveal reveal-delay-2 text-lg text-muted-foreground leading-relaxed">
              Each layer solves a specific constraint. <br />
              Each layer builds on the one before it. <br />
              <strong className="text-foreground">This is intentional</strong>.
            </p>
          </div>
        </section>

        {/* LAYER ZERO . STRATEGY FRONT DOOR */}
        <section className="py-16 bg-background border-b">
          <div className="container mx-auto px-6 max-w-5xl">
            <Link
              to="/solutions/ai-strategy-rollout/"
              className="reveal group block rounded-3xl border-2 border-violet-200 dark:border-violet-900 bg-violet-50/50 dark:bg-violet-950/20 p-8 md:p-10 hover:border-violet-400 shadow-soft hover:shadow-float transition-all lift-hover"
            >
              <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-800 dark:text-violet-300 text-xs font-bold uppercase tracking-wider mb-4">
                    <Layers className="w-3 h-3" /> Start Here: The Strategy Layer
                  </div>
                  <h3 className="text-3xl font-bold mb-3">AI Strategy, Implementation &amp; Rollout</h3>
                  <p className="text-muted-foreground max-w-2xl">
                    Not sure which layers you need? We meet with your team, audit your goals, tools, and
                    workflows, then build a phased roadmap, and implement it. This is the front door to
                    everything below.
                  </p>
                </div>
                <div className="flex items-center gap-2 font-bold text-violet-700 dark:text-violet-300 whitespace-nowrap">
                  Plan Before You Build
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* THE 5 LAYERS */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-6 max-w-5xl space-y-24">

            {/* Layer 1: CRM Growth */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="reveal inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300 text-xs font-bold uppercase tracking-wider mb-4">
                  <Database className="w-3 h-3" /> 1. The Foundation
                </div>
                <h3 className="reveal reveal-delay-1 text-4xl font-bold mb-4">CRM Growth</h3>
                <p className="text-lg font-medium text-foreground/90 mb-4">What it solves:</p>
                <p className="text-muted-foreground mb-6">Lead leakage, slow follow-up, operational chaos.</p>

                <p className="text-foreground mb-6">CRM Growth installs the central nervous system of your revenue:</p>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-teal-600" /> Every lead captured</li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-teal-600" /> Every conversation tracked</li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-teal-600" /> Every follow-up automated</li>
                </ul>

                <p className="text-sm text-muted-foreground italic mb-6">Without this layer, nothing else works reliably.</p>

                <Button variant="outline" asChild className="group border-teal-200 hover:border-teal-400 hover:bg-teal-50 dark:hover:bg-teal-950">
                  <Link to="/solutions/crm-automation/">
                    Explore CRM Growth <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
              <div className="hidden md:flex justify-center">
                <MissedCallTextback />
              </div>
            </div>

            <div className="w-full h-px bg-border" />

            {/* Layer 2: Marketing System */}
            <div className="grid md:grid-cols-2 gap-12 items-center md:flex-row-reverse">
              <div>
                <div className="reveal inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 text-xs font-bold uppercase tracking-wider mb-4">
                  <Zap className="w-3 h-3" /> 2. The Growth Engine
                </div>
                <h3 className="reveal reveal-delay-1 text-4xl font-bold mb-4">Marketing System</h3>
                <p className="text-lg font-medium text-foreground/90 mb-4">What it solves:</p>
                <p className="text-muted-foreground mb-6">Inconsistent demand and feast-or-famine revenue.</p>

                <p className="text-foreground mb-6">The Marketing System turns your foundation into motion by creating:</p>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-indigo-600" /> Inbound demand</li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-indigo-600" /> Outbound leadflow</li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-indigo-600" /> Database reactivation</li>
                </ul>

                <p className="text-sm text-muted-foreground italic mb-6">All feeding the same CRM. All tracked to revenue.</p>

                <Button variant="outline" asChild className="group border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950">
                  <Link to="/solutions/lead-generation-system/">
                    Explore the Growth Engine <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
              <div className="hidden md:flex justify-center">
                <LeadPipelineMockup />
              </div>
            </div>

            <div className="w-full h-px bg-border" />

            {/* Layer 3: Visibility Layer */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="reveal inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-bold uppercase tracking-wider mb-4">
                  <Globe className="w-3 h-3" /> 3. The Conversion Surface
                </div>
                <h3 className="reveal reveal-delay-1 text-4xl font-bold mb-4">Visibility Layer</h3>
                <p className="text-lg font-medium text-foreground/90 mb-4">What it solves:</p>
                <p className="text-muted-foreground mb-6">Being invisible, untrusted, or ignored online.</p>

                <p className="text-foreground mb-6">Visibility ensures attention turns into trust and traffic turns into leads:</p>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-blue-600" /> High-speed, conversion-grade websites</li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-blue-600" /> Local search & map presence</li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-blue-600" /> AI search visibility (ChatGPT, Perplexity, AI Overviews)</li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-blue-600" /> Automated review generation</li>
                </ul>

                <p className="text-sm text-muted-foreground italic mb-6">Visibility doesn’t replace systems. It feeds them.</p>

                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" asChild className="group border-blue-200 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950">
                    <Link to="/solutions/websites/">
                      Websites that convert <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                  <Button variant="outline" asChild className="group border-blue-200 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950">
                    <Link to="/solutions/local-seo-reputation/">
                      Local SEO & reviews <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>
              </div>
              <div className="hidden md:flex justify-center">
                <LocalPackMockup />
              </div>
            </div>

            <div className="w-full h-px bg-border" />

            {/* Layer 4: Scale System */}
            <div className="grid md:grid-cols-2 gap-12 items-center md:flex-row-reverse">
              <div>
                <div className="reveal inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-xs font-bold uppercase tracking-wider mb-4">
                  <TrendingUp className="w-3 h-3" /> 4. Volume Without Breakdown
                </div>
                <h3 className="reveal reveal-delay-1 text-4xl font-bold mb-4">Scale System</h3>
                <p className="text-lg font-medium text-foreground/90 mb-4">What it solves:</p>
                <p className="text-muted-foreground mb-6">Growth collapsing under volume or ad spend.</p>

                <p className="text-foreground mb-6">The Scale System introduces:</p>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-amber-600" /> Paid acceleration</li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-amber-600" /> High-volume lead handling</li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-amber-600" /> Performance visibility</li>
                </ul>

                <p className="text-sm text-muted-foreground italic mb-6">So growth stays profitable instead of chaotic.</p>

                <Button variant="outline" asChild className="group border-amber-200 hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950">
                  <Link to="/solutions/ad-management-scale/">
                    Explore the Scale System <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
              <div className="hidden md:flex justify-center rounded-3xl bg-slate-900 p-8">
                <ScaleRoasMockup />
              </div>
            </div>

            <div className="w-full h-px bg-border" />

            {/* Layer 5: Enterprise Intelligence */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="reveal inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-300 text-xs font-bold uppercase tracking-wider mb-4">
                  <Activity className="w-3 h-3" /> 5. Autonomous Operations
                </div>
                <h3 className="reveal reveal-delay-1 text-4xl font-bold mb-4">Enterprise Intelligence</h3>
                <p className="text-lg font-medium text-foreground/90 mb-4">What it solves:</p>
                <p className="text-muted-foreground mb-6">Human bottlenecks at scale.</p>

                <p className="text-foreground mb-6">Enterprise Intelligence replaces manual response, decision-making, and enforcement with real-time intelligence across teams and locations.</p>

                <p className="text-sm text-muted-foreground italic mb-6">This is infrastructure for high-volume operations.</p>

                <Button variant="outline" asChild className="group border-rose-200 hover:border-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950">
                  <Link to="/solutions/ai-strategy-rollout/">
                    Explore Enterprise Intelligence <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
              <div className="hidden md:flex justify-center">
                <VoiceAIMockup />
              </div>
            </div>

          </div>
        </section>

        <CapabilitiesSection />

        {/* HOW SYSTEMS WORK TOGETHER */}
        <section className="py-24 bg-slate-900 text-white">
          <div className="container mx-auto px-6 max-w-4xl text-center">
            <h2 className="reveal reveal-delay-1 text-3xl font-bold mb-4">How the Systems Work Together</h2>
            <p className="reveal reveal-delay-2 text-xl text-slate-300 mb-12">Not Optional. Sequential.</p>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12 mb-12">
              <div className="grid md:grid-cols-2 gap-8 text-left">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center shrink-0 font-bold text-xl">1</div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Each layer assumes the one below it exists</h3>
                    <p className="text-slate-400">You can't amplify traffic (Marketing) if you can't catch it (CRM).</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center shrink-0 font-bold text-xl">2</div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Each layer makes the one above it possible</h3>
                    <p className="text-slate-400">You can't automate decisions (Intelligence) without structured data (Growth).</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-lg text-slate-300 mb-4">
              Skipping layers creates fragility. <br />
              Building them in order creates leverage.
            </p>
            <p className="text-white font-bold">This is why Frayze engagements start with architecture, not tactics.</p>
          </div>
        </section>

        {/* INDUSTRIES */}
        <section className="py-24 bg-background border-b">
          <div className="container mx-auto px-6 max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="reveal reveal-delay-1 text-3xl font-bold mb-4">Automotive-Led. Cross-Industry Proven.</h2>
              <p className="reveal reveal-delay-2 text-lg text-muted-foreground">Frayze systems are built to adapt to industry realities, with deep specialization where it matters most.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Link to="/automotive/" className="reveal group p-8 border rounded-2xl bg-muted/20 hover:border-primary shadow-soft hover:shadow-float transition-all lift-hover">
                <h3 className="text-xl font-bold mb-4">Automotive</h3>
                <ul className="space-y-3 text-sm text-foreground/80">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Finance & inventory workflows</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> High inbound volume</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Missed-call recovery</li>
                </ul>
              </Link>
              <Link to="/industries/trades/" className="reveal reveal-delay-1 group p-8 border rounded-2xl bg-muted/20 hover:border-primary shadow-soft hover:shadow-float transition-all lift-hover">
                <h3 className="text-xl font-bold mb-4">Trades & Contractors</h3>
                <ul className="space-y-3 text-sm text-foreground/80">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Emergency lead handling</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Local visibility</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Dispatch-friendly pipelines</li>
                </ul>
              </Link>
              <Link to="/solutions/" className="reveal reveal-delay-2 group p-8 border rounded-2xl bg-muted/20 hover:border-primary shadow-soft hover:shadow-float transition-all lift-hover">
                <h3 className="text-xl font-bold mb-4">Professional & B2B</h3>
                <ul className="space-y-3 text-sm text-foreground/80">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Long-cycle nurture</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Outbound prospecting</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Contract pipelines</li>
                </ul>
              </Link>
            </div>

            <div className="text-center mt-12">
              <p className="font-bold text-lg">Different industries. Same system logic.</p>
            </div>
          </div>
        </section>

        {/* FUNDING & ACCESSIBILITY STRIP */}
        <section className="py-12 bg-muted/30 border-y">
          <div className="container mx-auto px-6 max-w-4xl">
            <Link to="/canada-ai-strategy-small-business/" className="reveal group flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl border bg-background p-6 shadow-soft transition-all hover:border-primary/30 hover:shadow-float lift-hover">
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-widest text-primary mb-1">Funding &amp; compliance</p>
                <h3 className="text-lg font-bold">Government funding may help pay for this, and accessibility may be required</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Canada's AI for All funding (FedNor in Northern Ontario) can offset an AI build, and AODA means many
                  Ontario sites must meet WCAG accessibility. See what applies to you.
                </p>
              </div>
              <span className="inline-flex items-center gap-1.5 font-bold text-primary whitespace-nowrap">
                Funding &amp; accessibility guide <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
        </section>

        {/* WHERE TO START */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-6 max-w-4xl text-center">
            <h2 className="reveal reveal-delay-1 text-3xl font-bold mb-12">Where Should You Start?</h2>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="reveal p-8 border rounded-2xl bg-background shadow-soft hover:border-teal-400 hover:shadow-float transition-all cursor-pointer lift-hover" onClick={() => window.location.href = '/solutions/crm-automation/'}>
                <h3 className="text-xl font-bold mb-2">Leads are leaking</h3>
                <div className="text-teal-700 font-bold mb-4">Start with CRM Growth</div>
                <p className="text-sm text-muted-foreground">Fix the bucket before you turn on the hose.</p>
              </div>
              <div className="reveal reveal-delay-1 p-8 border rounded-2xl bg-background shadow-soft hover:border-indigo-400 hover:shadow-float transition-all cursor-pointer lift-hover" onClick={() => window.location.href = '/solutions/lead-generation-system/'}>
                <h3 className="text-xl font-bold mb-2">Demand is inconsistent</h3>
                <div className="text-indigo-600 font-bold mb-4">Start with the Growth Engine</div>
                <p className="text-sm text-muted-foreground">Build a predictable flow of opportunities.</p>
              </div>
            </div>

            <p className="text-muted-foreground italic mb-12">We’ll help you decide in a short strategy session.</p>

            <div className="reveal bg-slate-950 text-white rounded-3xl p-12 text-center lift-hover">
              <h2 className="reveal reveal-delay-1 text-3xl font-bold mb-4">Build the Right System: In the Right Order</h2>
              <p className="reveal reveal-delay-2 text-lg text-slate-300 mb-8 max-w-xl mx-auto">
                You don’t need more tools. <br />
                You need a system that grows with you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-white text-slate-950 hover:bg-slate-200" asChild>
                  <Link to="/pricing/">View Pricing</Link>
                </Button>
                <Button size="lg" variant="outline" className="border-slate-700 text-white hover:bg-slate-800" asChild>
                  <Link to="/book-consultation/">Book a Strategy Session</Link>
                </Button>
              </div>
            </div>

          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default Solutions;